const Request = require('./request');
const Edition = require('core/src/edition').default;
const Function = require('core/src/function');
const _ = require('lodash');
const axios = require('axios').default;
const { isRunningNodeJs } = require("core/src/utils/platform");
const { delay } = require("core/src/utils/async");
const Err = require('utils/src/error');

const { fetchEventSource } = require('@microsoft/fetch-event-source');
const deferredPromise = require('core/src/utils/deferred-promise');

const log = require('core/src/log').instance("function/request");
const GRAPHILEON_APP_NAME_HEADER = 'Graphileon-App-Name';
const GRAPHILEON_ALLOWED_HOST_SUFFIXES = ['graphileon.com', 'graphileon.app'];

const removeHeaderIgnoreCase = (headers, headerName) => {
	_.forEach(_.keys(headers || {}), key => {
		if (_.toLower(key) === _.toLower(headerName)) {
			delete headers[key];
		}
	});
};

const parseUrl = url => {
	try {
		return new URL(url);
	} catch (error) {
		if (!_.startsWith(url, 'http://') && !_.startsWith(url, 'https://')) {
			try {
				return new URL(`https://${url}`);
			} catch (_parseError) {
				return null;
			}
		}
		return null;
	}
};

const shouldAttachGraphileonHeader = requestUrl => {
	const normalizedUrl = _.toLower(_.trim(_.toString(requestUrl || '')));
	if (!normalizedUrl) {
		return false;
	}

	if (_.includes(normalizedUrl, 'graphileon')) {
		return true;
	}

	const parsedUrl = parseUrl(normalizedUrl);
	if (!parsedUrl) {
		return false;
	}

	const hostname = _.toLower(parsedUrl.hostname || '');
	return _.some(
		GRAPHILEON_ALLOWED_HOST_SUFFIXES,
		suffix => hostname === suffix || _.endsWith(hostname, `.${suffix}`)
	);
};

const isRelativeUrl = url => _.startsWith(url, '/') && !_.startsWith(url, '//');

const isSameOrigin = (targetUrl, serverUrl) => {
	const target = parseUrl(targetUrl);
	const server = parseUrl(serverUrl);
	if (!target || !server) {
		return false;
	}

	return target.protocol === server.protocol
		&& _.toLower(target.hostname) === _.toLower(server.hostname)
		&& target.port === server.port;
};

Edition.setBuildFeature(Edition.Feature.FUNCTION_REQUEST, true);

let toURLSearchParams = (obj) => {
	return _.reduce(
		obj, 
		(res, val, key) => {
			res.append(key, val);
			return res;
		},
		new URLSearchParams()
	)
}

Request.prototype._streamController = undefined;
Request.prototype._fetchEventSource = undefined;
Request.prototype._streamRequest = undefined;

const createAbortError = () => {
	const error = new Error('Stream aborted');
	error.name = 'AbortError';
	return error;
};

const isAbortError = error =>
	_.get(error, 'name') === 'AbortError'
	|| _.get(error, 'name') === 'CanceledError'
	|| _.get(error, 'code') === 'ERR_CANCELED';

const createResponseLike = response => ({
	ok: response.status >= 200 && response.status < 300,
	status: response.status,
	headers: {
		get(name) {
			if (!response.headers) {
				return null;
			}
			return response.headers[String(name).toLowerCase()] || null;
		}
	}
});

const parseServerSentEvent = eventLines => {
	let hasData = false;
	const dataLines = [];

	for (const line of eventLines) {
		if (!line || _.startsWith(line, ':')) {
			continue;
		}

		const [field, ...rest] = _.split(line, ':');
		let value = rest.length ? rest.join(':') : '';

		value = _.trimStart(value, ' ');

		if (field === 'data') {
			hasData = true;
			dataLines.push(value);
		}
	}

	if (!hasData) {
		return null;
	}

	return {
		data: dataLines.join('\n')
	};
};

/**
 * Resolves a relative url against the configured Graphileon server. In a browser a relative url
 * resolves against the page origin, which is the hosting site when Graphileon is embedded in
 * another website — while a relative Request url is meant for the Graphileon server itself.
 * @param {string} url
 * @returns {string}
 */
Request.prototype.resolveUrl = function(url) {
	const requestUrl = _.trim(_.toString(url || ''));
	if (!isRelativeUrl(requestUrl)) {
		return url;
	}

	const api = this.api || {};
	const server = _.isFunction(api.getServerAddress)
		? api.getServerAddress()
		: (_.isFunction(api.getServer) ? api.getServer() : null);

	if (!_.isString(server) || _.isEmpty(_.trim(server))) {
		return url;
	}

	return _.trimEnd(_.trim(server), '/') + requestUrl;
};

/**
 * Whether a request goes to Graphileon's own server, and therefore needs to carry the session
 * cookie. When Graphileon is embedded in another site these requests become cross-site, and
 * browsers only attach cookies to cross-site requests that explicitly ask for credentials.
 * @param {string} url
 * @returns {boolean}
 */
Request.prototype.targetsGraphileonServer = function(url) {
	const requestUrl = _.trim(_.toString(url || ''));
	if (!requestUrl || isRelativeUrl(requestUrl)) {
		return true;
	}

	const server = _.isFunction(_.get(this, 'api.getServer')) ? this.api.getServer() : null;
	if (!_.isString(server) || _.isEmpty(_.trim(server))) {
		return false;
	}

	return isSameOrigin(requestUrl, _.trim(server));
};

Request.prototype.getGraphileonAppName = function() {
	return _.trim(_.toString(_.get(this.appInfo, 'application.name') || 'Graphileon'));
};

Request.prototype.addGraphileonAppNameHeader = function(request) {
	const nextRequest = _.cloneDeep(request) || {};
	const headers = _.cloneDeep(_.get(nextRequest, 'headers')) || {};
	const appName = this.getGraphileonAppName();
	const shouldAttachHeader = shouldAttachGraphileonHeader(_.get(nextRequest, 'url'));

	removeHeaderIgnoreCase(headers, GRAPHILEON_APP_NAME_HEADER);

	if (!appName || !shouldAttachHeader) {
		nextRequest.headers = headers;
		return nextRequest;
	}

	headers[GRAPHILEON_APP_NAME_HEADER] = appName;
	nextRequest.headers = headers;
	return nextRequest;
};

Request.prototype.execute = function (...args) {
	if(!Edition.hasFeature(Edition.Feature.FUNCTION_REQUEST)) {
		const error = Edition.functionNotAvailableError('Request');
		this.error(error);
		throw error;
	}
	return Function.prototype.execute.call(this, ...args);
};

Request.prototype.makeRequest = async function(request) {
	let req = this.addGraphileonAppNameHeader(request);

	if (	_.get(request, ['headers','content-type']) === 'application/x-www-form-urlencoded'
		||	_.get(request, ['headers','Content-Type']) === 'application/x-www-form-urlencoded'
	) {
		req.data = toURLSearchParams(req.data);
	}

	if (!_.has(req, 'withCredentials') && this.targetsGraphileonServer(req.url)) {
		req.withCredentials = true;
	}

	return axios(req);
};

Request.prototype.makeNodeStreamRequest = async function(request, callbacks) {
	request = this.addGraphileonAppNameHeader(request);

	let response;
	let isClosed = false;
	let leftover = '';
	let currentEventLines = [];
	let finalizeStream;
	const streamFinished = new Promise(resolve => {
		finalizeStream = resolve;
	});

	const cleanup = () => {
		this._streamController.signal.removeEventListener('abort', abortHandler);
		this._streamResponse = undefined;
		this._streamRequest = undefined;
		finalizeStream();
	};

	const closeOnce = () => {
		if (isClosed) {
			return;
		}
		isClosed = true;
		cleanup();
		callbacks.onclose();
	};

	const errorOnce = (error) => {
		if (isClosed) {
			return;
		}
		isClosed = true;
		cleanup();
		callbacks.onerror(error);
	};

	const flushCurrentEvent = () => {
		const parsedEvent = parseServerSentEvent(currentEventLines);
		currentEventLines = [];
		if (parsedEvent) {
			callbacks.onmessage(parsedEvent);
		}
	};

	const handleChunk = chunk => {
		leftover += chunk;

		let lineBreakIndex = leftover.indexOf('\n');
		while (lineBreakIndex !== -1) {
			let line = leftover.slice(0, lineBreakIndex);
			leftover = leftover.slice(lineBreakIndex + 1);

			if (line.endsWith('\r')) {
				line = line.slice(0, -1);
			}

			if (line === '') {
				flushCurrentEvent();
			} else {
				currentEventLines.push(line);
			}

			lineBreakIndex = leftover.indexOf('\n');
		}
	};

	const finishStream = () => {
		if (leftover) {
			currentEventLines.push(leftover.endsWith('\r') ? leftover.slice(0, -1) : leftover);
			leftover = '';
		}
		if (currentEventLines.length > 0) {
			flushCurrentEvent();
		}
		closeOnce();
	};

	const abortHandler = () => {
		if (!_.get(response, 'data.destroyed')) {
			response.data.destroy(createAbortError());
		} else {
			closeOnce();
		}
	};

	this._streamController.signal.addEventListener('abort', abortHandler);

	try {
		const axiosOptions = {
			..._.omit(request, ['body']),
			data: _.has(request, 'body') ? request.body : request.data,
			responseType: 'stream',
			signal: this._streamController.signal,
			validateStatus: () => true,
		};

		response = await axios(axiosOptions.url, axiosOptions);

		const responseLike = createResponseLike(response);
		await callbacks.onopen(responseLike);
		if (this._streamController.signal.aborted || !responseLike.ok) {
			cleanup();
			return;
		}

		this._streamResponse = response.data;
		if (_.isFunction(response.data.setEncoding)) {
			response.data.setEncoding('utf8');
		}

		response.data.on('data', handleChunk);
		response.data.on('end', finishStream);
		response.data.on('close', () => {
			if (this._streamController.signal.aborted) {
				closeOnce();
			}
		});
		response.data.on('error', error => {
			if (isAbortError(error) || this._streamController.signal.aborted) {
				closeOnce();
				return;
			}
			errorOnce(error);
		});
	} catch (error) {
		if (isAbortError(error) || this._streamController.signal.aborted) {
			closeOnce();
			return;
		}
		errorOnce(error);
	}

	await streamFinished;
};

Request.prototype.makeServerStreamRequest = function(request) {
	const headers = {
		'Content-Type': 'application/json',
	};

	// `Cookie` is a forbidden header name in browsers: scripts cannot set it and it is stripped
	// silently. There the session cookie has to travel with the request itself, which
	// `makeStreamRequest` arranges by asking for credentialed CORS.
	if (this.api.token && isRunningNodeJs()) {
		headers.Cookie = `token=${this.api.token}`;
	}

	const proxyRequest = {
		url: this.api.getServer() + '/api/request/stream',
		method: 'POST',
		data: {
			request: _.pick(request, 'method', 'data', 'headers', 'params', 'timeout', 'urlData', 'responseType'),
			requestFunctionId: this.id
		},
		headers,
		responseType: 'stream',
		withCredentials: true
	};

	return this.makeStreamRequest(proxyRequest);
};

Request.prototype.makeStreamRequest = async function(request) {
	request = this.addGraphileonAppNameHeader(request);

	if (_.get(request, 'responseType') !== 'stream') {
		return this.makeRequest(request);
	}
	const self = this;
	const req = _.cloneDeep(request);
	const finisher = this.readModel('finisher');
	const bufferOptions = this.readModel('buffer');
    const messageCount = _.get(bufferOptions, 'messageCount');
    const timeLimit = _.get(bufferOptions, 'timeLimit');
	const queueProcessDelay = this.readModel("queueProcessDelay") || 5;

	req.body = JSON.stringify(req.data);
	let content = [];
	this._streamController = new AbortController();

    let messageBuffer = [];
    let bufferTimeout = null;

	const triggerQueue = [];
	let isProcessingQueue = false;
	let streamClosed = false;

	let resolveFinished, rejectFinished;
    const rawFinishedPromise = new Promise((res, rej) => {
        resolveFinished = res;
        rejectFinished = rej;
    });
    const finishedPromise = deferredPromise(rawFinishedPromise);


	const processQueue = async () => {
		if (isProcessingQueue) {
			return;
		}
		isProcessingQueue = true;

		while (triggerQueue.length > 0) {
			const payload = triggerQueue.shift();
			await self.executeTriggers(payload);
			await delay(queueProcessDelay);
		}

		isProcessingQueue = false;

		// If the stream has ended and the queue is now empty, fire the finished event.
		if (streamClosed && triggerQueue.length === 0) {
			try {
				await self.executeTriggers({
					type: "finished",
					data: content,
				});
			} finally {
				resolveFinished();
			}
		}
	};

	const clearTimer = () => {
		if (bufferTimeout) {
			clearTimeout(bufferTimeout);
			bufferTimeout = null;
		}
	}
	const addMessagesToQueue = () => {
		if (messageBuffer.length > 0) {
			const bufferedData = messageBuffer;
			triggerQueue.push({
				type: "messageReceived",
				data: bufferedData,
			});
			messageBuffer = [];
		}
	}

	const flushBuffer = () => {
		clearTimer();
		addMessagesToQueue();
		processQueue();
	};

	const handleMessage = event => {
		const data = event.data;
		content.push(data);

		messageBuffer.push(data);

		if (messageCount && messageBuffer.length >= messageCount) {
			// buffer { messageCount }
			flushBuffer();
		} else if (timeLimit && !bufferTimeout) {
			// buffer { timeLimit }
			bufferTimeout = setTimeout(flushBuffer, timeLimit);
		} else if(!messageCount && !timeLimit){
			// buffer {} || !buffer
			flushBuffer();
		}

		if (!finisher || (!_.isString(finisher) && !_.isFunction(finisher))) {
			return;
		}

		let finished = false;
		if (_.isString(finisher) && data.includes(finisher)) {
			finished = true;
		}

		if (_.isFunction(finisher) && finisher.call(null, data)) {
			finished = true;
		}

		if (finished) {
			flushBuffer(); // Queue any remaining messages
			this._streamController.abort();
		}
	};

	const handleOpen = response => {
		if (response.ok) {
			return;
		}

		// For any non-ok response, we want to stop.
		// We create an error but don't throw it, to avoid the library's retry.
		const err = new Error(`Stream connection failed with status ${response.status}`);

		_.defer(async () => {
			await self.executeTriggers({
				type: 'error',
				error: err
			});
		});

		this._streamController.abort();
		rejectFinished(err);
	};

	const handleClose = () => {
		streamClosed = true;
		flushBuffer(); // Queue any final messages
	};

	const handleError = error => {
		streamClosed = true;
		if (isAbortError(error)) {
			clearTimer();
			messageBuffer = [];
			return;
		}

		_.defer(async () => {
			await self.executeTriggers({
				type: 'error',
				error
			});
		});
		flushBuffer(); // Queue any final messages
		rejectFinished(error);
	};

	if (isRunningNodeJs()) {
		this._streamRequest = this.makeNodeStreamRequest(req, {
			onmessage: handleMessage,
			onopen: handleOpen,
			onclose: handleClose,
			onerror: handleError
		});
	} else {
		this._fetchEventSource = fetchEventSource(req.url, {
			..._.omit(req, ['url', 'data', 'withCredentials']),
			// Keep the existing stream alive when the tab is hidden instead of reconnecting on return.
			openWhenHidden: true,
			// Without this the session cookie is dropped once Graphileon is embedded in another site.
			credentials: this.targetsGraphileonServer(req.url) ? 'include' : 'same-origin',
			signal: this._streamController.signal,
			onmessage: handleMessage,
			onopen: handleOpen,
			onclose: handleClose,
			onerror: handleError
		});
	}

	return finishedPromise;
}

Request.prototype.makeServerRequest = async function(request) {
	request = this.addGraphileonAppNameHeader(request);

	const data = {
		request: _.pick(request, 'method', 'data', 'headers', 'params', 'timeout', 'urlData', 'responseType'),
		requestFunctionId: this.id
	};
	if (_.get(request, 'timeout')) {
		data.timeout = request.timeout;
	}
	const response = await this.api.executeRequest('/request/execute', 'post', data);
	if('error' in response) {
		// Convert error response to Axios-style error
		const err = new Error(response.error.message);
		err.response = response.error.response;
		throw err;
	}
	return response.success;
};

Request.prototype.onExecute = async function() {
	const model = this.readModel();

	// Backward compatibility
	const jqueryParams = _.filter(Request.jQueryParameters, param => !_.isNil(model[param]));
	if(jqueryParams.length > 0) {
		log.warn(`The following Request parameters are deprecated and will be removed in following versions of Graphileon: ` +
			jqueryParams.join(", ") +
			". Please refer to the documentation at https://docs.graphileon.com/graphileon/For_Dashboard_Designers/Reference/Request.html.");
		return this.jqueryRequest(model);
	}

	// Normal mode, using Axios
	const requestConfig = _.pick(model, 'url', 'method', 'data', 'headers', 'params', 'timeout', 'auth', 'urlData', 'responseType');
	requestConfig.url = this.resolveUrl(requestConfig.url);
	const request = this.addGraphileonAppNameHeader(requestConfig);
	if(model.download) {
		request.responseType = 'blob';
	}

	let response;
	let isShowingLoader;
	let showingLoaderTimeout;
	try {
		if (_.isFinite(model.showLoaderAfterMs) && model.showLoaderAfterMs > 0 && model.responseType !== 'stream') {
			showingLoaderTimeout = setTimeout(
				() => { 
					showingLoaderTimeout = null;
					isShowingLoader = true;
					this.api.loadingRequestStarted();
				},
				model.showLoaderAfterMs
			)
		}

		if(model.serverSide === true && (!isRunningNodeJs() || this.forceAllowServerSide || !this.fti._frontend)) {
			if (model.responseType === 'stream') {
				await this.makeServerStreamRequest(request);
				return;
			}
			response = await this.makeServerRequest(request);
		} else if (model.responseType === 'stream') {
			await this.makeStreamRequest(request);
			return;
		}else {
			response = await this.makeRequest(request);
		}

		if (showingLoaderTimeout) {
			clearTimeout(showingLoaderTimeout);
			showingLoaderTimeout = null;
		}

		if (isShowingLoader) {		
			isShowingLoader = false;
			this.api.loadingRequestFinished();
		}
		if(model.download === true) {
			// const extension = _.get(/\.\w+$/.exec(request.url), [0], '');
			this.download(response.data, model.downloadName);
			response.data = model.downloadName;
		}
	} catch(e) {
		if (showingLoaderTimeout) {
			clearTimeout(showingLoaderTimeout);
			showingLoaderTimeout = null;
		}
		if (isShowingLoader)	{
			isShowingLoader = false;
			this.api.loadingRequestFinished();
		}
		const finalResponse = this.standardizeResponse(e);
		for(let type of ['error', 'fail']) { // `fail` for backward compatibility
			this.executeTriggers({
				type,
				error: _.get(e, 'message'),
				completed: true, // so one can easily create Triggers for both success and failure
				response: finalResponse
			});
		}
		if(model.allowFailure !== true) {
			this.error(e);
		}
		return;
	}

	const finalResponse = this.standardizeResponse(response);
	const triggerData = {
		type: 'success',
		completed: true, // so one can easily create Triggers for both success and failure
		response: finalResponse
	};
	if('error' in finalResponse) {
		// for consistency with the 'error' trigger
		triggerData.error = finalResponse.error;
	}
	this.executeTriggers(triggerData);
};

Request.prototype.download = function(data, fileName) {
	const downloadUrl = window.URL.createObjectURL(new Blob([data]));

	const link = document.createElement('a');
	link.href = downloadUrl;
	link.setAttribute('download', fileName);

	document.body.appendChild(link);

	link.click();
	link.remove();
};

/**
 * For backward compatibility
 * @param model
 * @returns {Promise<unknown>}
 */
Request.prototype.jqueryRequest = function(model) {
	const ajaxSettings = {};
	for(let key of [...Request.jQueryParameters, 'url', 'data', 'headers', 'method']) {
		if(key in model) {
			ajaxSettings[key] = model[key];
		}
	}
	if (_.isString(ajaxSettings.url)) {
		ajaxSettings.url = this.resolveUrl(ajaxSettings.url);
	}
	// if requestDataType = JSON transform data to JSON
	if (model.requestDataType && (model.requestDataType.toString().trim().toLowerCase() === 'json') && ! _.isString(ajaxSettings.data)) {
		ajaxSettings.data = ajaxSettings.data && JSON.stringify(ajaxSettings.data);
	}
	if(typeof $ == 'undefined' || !_.isFunction($.ajax)) {
		throw new Error("jQuery Requests cannot be executed from the server. " +
			"Use only currently documented parameters to prevent dependency on jQuery.");
	}

	const request = $.ajax(ajaxSettings);

	const self = this;
	return new Promise(async (resolve, reject) => {
		if (model.download) {
			request.success(
				function(response, status, xhr) {
					// check for a filename
					let filename = "";

					const disposition = xhr.getResponseHeader('Content-Disposition');
					if (disposition && disposition.indexOf('filename') !== -1) {
						const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
						const matches = filenameRegex.exec(disposition);
						if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
					}

					const type = xhr.getResponseHeader('Content-Type');
					const blob = new Blob([response], { type: type });

					if (typeof window.navigator.msSaveBlob !== 'undefined') {
						// IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for which they were created. These URLs will no longer resolve as the data backing the URL has been freed."
						window.navigator.msSaveBlob(blob, filename);
						return;
					}

					const URL = window.URL || window.webkitURL;
					const downloadUrl = URL.createObjectURL(blob);

					const a = document.createElement("a");
					// safari doesn't support this yet
					if (typeof a.download === 'undefined') {
						// window.open(downloadUrl);
					} else {
						a.href = downloadUrl;
						a.download = filename || model.downloadName;
						document.body.appendChild(a);
						a.click();
					}
					setTimeout(function () { URL.revokeObjectURL(downloadUrl); }, 100); // cleanup
					resolve();
				}
			);
		}

		request.always(function(response) {
			// Error
			let type = 'success';
			if(Request.isXHRResponse(response) && response.status !== 200) {
				type = 'fail';
				const errorMessage = self.findErrorMessage(response);
				let originalError = undefined;
				if(_.isString(errorMessage)) {
					originalError = new Err(errorMessage);
				} else {
					originalError = new Err(response.status + " " + response.statusText);
				}
				if(!_.hasBooleanValue(model.allowFailure, true)) {
					reject(new Err(self.language.translate('Ajax request failed ({{status}})', {status: response.status + " " + response.statusText}), originalError));
				} else {
					resolve();
				}
			}

			self.executeTriggers({
				type: type,
				completed: true, // so one can easily create Triggers for both success and failure
				response: self.standardizeResponse(response)
			});
		});
	});
};
